import type {
  Asset,
  Assumptions,
  ClientCreate,
  ExampleClientOpen,
  ExampleClientOpenResponse,
  ClientPlan,
  ClientRead,
  ClientStatus,
  Firm,
  AdviserProfile,
  IncomeStream,
  IsoDate,
  Liability,
  LifeEvent,
  ReportExport,
  ClientListItem,
  TeamMember,
  Scenario,
  SpendingItem,
  WealthProjectionRequest,
  WealthProjectionResult,
} from '@cf/shared';

export type PlanItemKind = 'asset' | 'incomeStream' | 'spendingItem' | 'liability' | 'lifeEvent';

export interface ClientListQuery {
  search?: string;
  status?: ClientStatus | 'all';
}

/**
 * The single data-access boundary for every screen. Backed by fixtures today;
 * the real /api/v1 client implements the same interface later. All parameter
 * and return shapes are @cf/shared contracts — no ad-hoc types cross this line.
 */
/** The practice example is not available right now (API 503 `example_unavailable`). */
export class ExampleUnavailableError extends Error {
  constructor(
    message: string,
    /** UX-01: the server's Retry-After, so the screen can offer Try again when it will help. */
    readonly retryAfterSeconds = 60,
  ) {
    super(message);
    this.name = 'ExampleUnavailableError';
  }
}

export interface IPlanDataClient {
  /** The injected "today". No render path reads the wall clock. */
  getAsOfDate(): IsoDate;
  getStartYear(): number;

  getFirm(): Promise<Firm>;
  getAdviser(): Promise<AdviserProfile>;

  /** The LIST view: each row carries how far its plan has got. */
  listClients(query: ClientListQuery): Promise<ClientListItem[]>;
  getClient(clientId: string): Promise<ClientRead>;
  createClient(input: ClientCreate): Promise<ClientRead>;
  /**
   * Workspace v2 §14 — open (create or resume) the signed-in adviser's OWN
   * practice example. Identity is the server's (`isExample`), never the name.
   * Rejects with {@link ExampleUnavailableError} when the example cannot be
   * served yet (a database behind its migrations).
   */
  openExampleClient(clock: ExampleClientOpen): Promise<ExampleClientOpenResponse>;
  /** "Reset example": back to its starting figures. */
  resetExampleClient(clock: ExampleClientOpen): Promise<ExampleClientOpenResponse>;
  archiveClient(clientId: string): Promise<ClientRead>;
  restoreClient(clientId: string): Promise<ClientRead>;
  /**
   * The firm's advisers — the pool the owner may hand a client to. Owner-only
   * upstream; a non-owner never opens the control that needs it.
   */
  listTeamMembers(): Promise<TeamMember[]>;
  /**
   * Hand a client to another adviser. OWNER-ONLY (firm principal's decision);
   * the API and migration 0033's policies both enforce it.
   */
  reassignClient(clientId: string, adviserId: string): Promise<void>;
  /** Soft delete (`deleted_at`) an ARCHIVED client. Never a hard delete. */
  deleteClient(clientId: string): Promise<void>;

  getPlan(clientId: string): Promise<ClientPlan>;
  upsertAsset(clientId: string, item: Asset): Promise<ClientPlan>;
  upsertIncomeStream(clientId: string, item: IncomeStream): Promise<ClientPlan>;
  upsertSpendingItem(clientId: string, item: SpendingItem): Promise<ClientPlan>;
  upsertLiability(clientId: string, item: Liability): Promise<ClientPlan>;
  upsertLifeEvent(clientId: string, item: LifeEvent): Promise<ClientPlan>;
  removePlanItem(clientId: string, kind: PlanItemKind, itemId: string): Promise<ClientPlan>;

  getAssumptions(clientId: string): Promise<Assumptions>;
  updateAssumptions(clientId: string, next: Assumptions): Promise<Assumptions>;

  listScenarios(clientId: string): Promise<Scenario[]>;
  saveScenario(clientId: string, scenario: Scenario): Promise<Scenario>;
  deleteScenario(clientId: string, scenarioId: string): Promise<void>;

  runWealthProjection(request: WealthProjectionRequest): Promise<WealthProjectionResult>;
  getReportData(clientId: string, scenarioId: string | null): Promise<ReportExport>;
}
